import React from "react";
import { Avatar, Divider, Layout, Menu, Space, Typography } from "antd";
import {
  ApartmentOutlined,
  BlockOutlined,
  ContainerOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  LinkOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { collapsedSidebarWidth, sidebarWidth } from "../themeTokens";
import { useAuth } from "../context/AuthContext";
import { canAccessRoute } from "../constants/permissions";

const NAV_ITEMS = [
  {
    type: "group",
    label: "Core",
    children: [
      { key: "/dashboard", icon: <DashboardOutlined />, label: "Dashboard" },
      { key: "/users", icon: <TeamOutlined />, label: "Users" },
    ],
  },
  {
    type: "group",
    label: "Operations",
    children: [
      { key: "/plans", icon: <ContainerOutlined />, label: "Plans" },
      { key: "/customers", icon: <ApartmentOutlined />, label: "Customers" },
      { key: "/subscriptions", icon: <ShoppingOutlined />, label: "Subscriptions" },
      { key: "/payments", icon: <CreditCardOutlined />, label: "Payments" },
    ],
  },
  {
    type: "group",
    label: "Configuration",
    children: [
      { key: "/features", icon: <BlockOutlined />, label: "Features" },
      { key: "/plan-features", icon: <LinkOutlined />, label: "Plan Features" },
    ],
  },
];

// Groups whose every child is hidden are dropped so the role never sees an
// empty section heading.
const visibleNavItems = (user) =>
  NAV_ITEMS.map((group) => ({
    ...group,
    children: group.children.filter((item) => canAccessRoute(user, item.key)),
  })).filter((group) => group.children.length > 0);

const Sidebar = ({ collapsed = false, mobile = false, onNavigate, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const navItems = React.useMemo(() => visibleNavItems(user), [user]);

  const handleNavigate = ({ key }) => {
    navigate(key);
    onNavigate?.(key);
    onClose?.();
  };

  const content = (
    <div className="sidebar-shell">
      <div className="sidebar-brand">
        <Space size={12} align="center">
          <Avatar
            shape="square"
            size={collapsed ? 44 : 48}
            style={{
              background: "linear-gradient(135deg, #4b7cf3 0%, #42baf9 100%)",
              fontWeight: 700,
            }}
          >
            VV
          </Avatar>
          {!collapsed && (
            <div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Virtuevise
              </Typography.Title>
              <Typography.Text type="secondary">Admin Portal</Typography.Text>
            </div>
          )}
        </Space>
      </div>

      <div className="sidebar-menu">
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={navItems}
          inlineCollapsed={collapsed}
          onClick={handleNavigate}
          style={{ borderInlineEnd: 0 }}
        />
      </div>

      {!collapsed && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <div className="sidebar-footer">

            <Typography.Text strong> Virtuevise Admin Portal © {new Date().getFullYear()}</Typography.Text>


          </div>
        </>
      )}
    </div>
  );

  if (mobile) {
    return <div style={{ width: "100%" }}>{content}</div>;
  }

  return (
    <Layout.Sider
      width={sidebarWidth}
      collapsedWidth={collapsedSidebarWidth}
      collapsed={collapsed}
      trigger={null}
      theme="light"
      className="app-sider"
    >
      {content}
    </Layout.Sider>
  );
};

export default Sidebar;
